"use client";

import { useEffect, useRef } from "react";

import { devSectionAttr } from "@/lib/dev/section-attr";
import { useCcPrefersReducedMotion } from "@/lib/use-cc-prefers-reduced-motion";

import { geoIcon } from "./geo-icons";
import type { GeoSubpageCopy } from "./geo-page-copy";

/**
 * Nouva-style statement: the words light up one by one as the section
 * scrolls through the viewport. The scroll handler only writes `--p`; CSS
 * works out each word's opacity from its index. Offset cards follow.
 */
export function CcGeoStatement({
  copy,
}: {
  copy: GeoSubpageCopy["statement"];
}) {
  const ref = useRef<HTMLDivElement>(null);
  const reduced = useCcPrefersReducedMotion();
  const words = copy.text.split(" ");

  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    if (reduced) {
      node.style.setProperty("--p", "1");
      return;
    }
    let frame = 0;
    const update = () => {
      frame = 0;
      const rect = node.getBoundingClientRect();
      const progress =
        (window.innerHeight * 0.85 - rect.top) /
        (rect.height + window.innerHeight * 0.35);
      node.style.setProperty(
        "--p",
        Math.min(1, Math.max(0, progress)).toFixed(3),
      );
    };
    const onScroll = () => {
      if (!frame) frame = requestAnimationFrame(update);
    };
    // Only listen to scroll while the text is near the viewport.
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        update();
        window.addEventListener("scroll", onScroll, { passive: true });
      } else {
        window.removeEventListener("scroll", onScroll);
      }
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
      window.removeEventListener("scroll", onScroll);
      cancelAnimationFrame(frame);
    };
  }, [reduced]);

  return (
    <section
      className="cc-section cc-geo-statement"
      id="geo-why-now"
      aria-labelledby="cc-geo-statement-title"
      data-cc-nav="light"
      {...devSectionAttr("geo-why-now")}
    >
      <div className="cc-geo-statement__container">
        <p className="cc-geo-mono">{copy.eyebrow}</p>
        <div
          ref={ref}
          className="cc-geo-statement__text"
          style={{ ["--n" as string]: words.length }}
        >
          <h2 id="cc-geo-statement-title">
            {words.map((word, i) => (
              <span key={i} style={{ ["--i" as string]: i }}>
                {word}{" "}
              </span>
            ))}
          </h2>
        </div>
        <ul className="cc-geo-statement__cards">
          {copy.cards.map((card, i) => {
            const Icon = geoIcon(card.icon);
            return (
              <li key={card.icon} style={{ ["--i" as string]: i }}>
                <i aria-hidden="true">
                  <Icon size={20} strokeWidth={2} />
                </i>
                <h3>{card.title}</h3>
                <p>{card.text}</p>
              </li>
            );
          })}
        </ul>
      </div>
    </section>
  );
}
